import React, {useState} from 'react';
import TopBar from '../../Components/TopBar';
import Page from '../../Components/Containers/Page';
import { useBalance, useInvoices } from '../../Data/Balance';
import { PageLoading } from '../../Components/Loading';
import Button from '../../Components/Button/Button';
import { useDownloadItemisation, numFormat, ucFirst, classNames } from '../../Services/helpers';
import DateTime from '../../Components/Formatting/DateTime';
import Money from '../../Components/Formatting/Money';
import { useNavigate } from 'react-router-dom';
import AddCard from './AddCard';
import AddSMSCredits from './AddSMSCredits';
import AddLeadCredits from './AddLeadCredits';
import AddVoucher from './AddVoucher';
import { useSession } from '../../Components/Session';
import PendingVoucher from './PendingVoucher';
import TopUp from './TopUp';
import { enableNewPlans, usePlans } from '../../Data/Plans';

const Billing = () => {
    // Make sure we refresh this so pending vouchers are picked up
    const {refresh: refreshSession} = useSession();
    const {balance, paymentMethod, refresh: refreshBalance} = useBalance(true);
    const {invoices, refresh: refreshInvoices} = useInvoices(true);
    const {plans} = usePlans();

    const refresh = async () => {
        await Promise.all([
            refreshBalance(),
            refreshInvoices(),
            refreshSession(),
        ])
    }

    return <>
        <TopBar left="/more" title="Billing & Plan" />
        <Page>
            <PendingVoucher onComplete={refresh} />
            {balance ? <Inner {...{plans, balance, paymentMethod, invoices, refresh}} /> : <PageLoading />}
        </Page>
    </>
}

const Inner = ({plans, balance, paymentMethod, invoices, refresh}) => {
    const navigate = useNavigate();
    const [addingCard, setAddingCard] = useState(false);
    const [addingSMS, setAddingSMS] = useState(false);
    const [addingLeads, setAddingLeads] = useState(false);
    const [addingVoucher, setAddingVoucher] = useState(false);
    const [toppingUp, setToppingUp] = useState(false);
    const currentPlan = (plans && balance) ? plans.find(p => p.name === balance.plan) : null;

    return <>
        <div className="flex flex-col divide-y w-full">
            <Row hide={balance.retainedPence === 0}
                title="Account balance"
                info={<Money colour symbol pence={balance.retainedPence} />}
                right={<Button theme={balance.retainedPence > 0 ? 'light' : 'danger'}
                    label="Top up"
                    onClick={() => setToppingUp(true)}
                    zeroMargin
                />}
            />
            <Row title="Plan"
                info={<>
                    {balance.plan} ({balance.subscriptionPeriod})<br />
                    {balance.subscriptionPeriod !== 'Forever' && <span className="text-muted">
                        Renews <DateTime date={balance.anniversary} hideTime />
                    </span>}
                    {(enableNewPlans && currentPlan && currentPlan.legacy) && <><br />
                        <span className="text-red-600 font-bold">
                            Legacy plan, please upgrade
                        </span>
                    </>}
                </>}
                right={<Button theme="primary" label="Upgrade" zeroMargin onClick={() => navigate('/billing/plan')} />}
            />
            <Row title="Payment method"
                info={<PaymentMethod {...{paymentMethod, balance}} />}
                right={<Button theme={paymentMethod ? 'light' : 'primary'}
                    label={paymentMethod ? 'Change' : 'Add'}
                    onClick={() => setAddingCard(true)}
                    zeroMargin
                />}
            />
            <Row hide={!balance.planCredits}
                title="Remaining months of Pro plan"
                info={numFormat(balance.planCredits)}
                right={null}
            />
            <Row title="Lead Credits"
                info={numFormat(balance.leadCredits)}
                right={<Button theme="light" label="Top up" zeroMargin onClick={() => setAddingLeads(true)} />}
            />
            <Row title="SMS Credits"
                info={<>
                    <span className={classNames(
                        balance.sms <= 0 && 'text-red-600 font-bold',
                        (balance.sms < 50 && balance.sms > 0) && 'text-amber-500 font-bold',
                        )}>
                        {numFormat(balance.sms)}
                    </span><br />
                    {balance.autoTopUpSMS && <span className="text-muted">
                        Auto top up {numFormat(balance.autoTopUpSMS)} credits
                    </span>}
                </>}
                right={<Button theme="light" label="Top up" zeroMargin onClick={() => setAddingSMS(true)} />}
            />
            <Row title="Vouchers"
                info={<>
                    {balance.vouchersUsed.length === 0 && <span className="text-muted text-sm">None</span>}
                    {balance.vouchersUsed.map((voucher, i) => {
                        return <span key={voucher+i}>{voucher}<br /></span>
                    })}
                </>}
                right={<Button theme="light" label="Add voucher" zeroMargin onClick={() => setAddingVoucher(true)} />}
            />
        </div>
        <Invoices invoices={invoices} />

        <TopUp isOpen={toppingUp} setIsOpen={setToppingUp} after={refresh} />
        <AddCard isOpen={addingCard} setIsOpen={setAddingCard} onComplete={refresh} />
        <AddSMSCredits isOpen={addingSMS} setIsOpen={setAddingSMS} onComplete={refresh} />
        <AddLeadCredits isOpen={addingLeads} setIsOpen={setAddingLeads} onComplete={refresh} />
        <AddVoucher isOpen={addingVoucher} setIsOpen={setAddingVoucher} onComplete={refresh} />
    </>
}

const Row = ({hide, title, info, right}) => {
    if (hide) {
        return null;
    }

    return <div className="flex flex-row justify-between py-4">
        <div className="grow">
            <p className="font-bold mb-2">
                {title}
            </p>
            <p>
                {info}
            </p>
        </div>
        <div className="shrink-0 flex items-center">
            {right}
        </div>
    </div>
}

const PaymentMethod = ({paymentMethod, balance}) => {
    if (paymentMethod === null) {
        return <span className="text-muted">None</span>
    }
    const expired = new Date(paymentMethod.card.exp_year, paymentMethod.card.exp_month) < new Date();

    return <>
        {ucFirst(paymentMethod?.card?.brand)} &nbsp; &nbsp;
        &bull;&bull;&bull;&bull;
        &nbsp;
        &bull;&bull;&bull;&bull;
        &nbsp;
        &bull;&bull;&bull;&bull;
        &nbsp;
        {paymentMethod?.card?.last4}
        <br />
        <span className={expired ? 'text-red-600 font-bold' : 'text-muted'}>
            (Expires {paymentMethod?.card?.exp_month}/{paymentMethod?.card?.exp_year})
        </span>
        {balance.paymentMethodError && <p className="mt-3 text-red-600 font-bold">
            Please update your payment card.<br />
            The last payment failed with error:<br />
            {balance.paymentMethodError}<br />
        </p>}
    </>
}

const Invoices = ({invoices}) => {
    const downloadItemisation = useDownloadItemisation();

    if (!invoices) {
        return null;
    }

    return <div className="flex flex-col my-8">
        <h3 className="font-bold mb-2">Invoices</h3>
        {invoices.length === 0 && <p className="text-muted text-center">No invoices</p>}

        {invoices.map((invoice) => {
            return <div key={invoice.id} className="grid grid-cols-4 pb-4 mt-4 border-b">
                <div className="font-bold">
                    <Money pence={invoice.total} />
                </div>
                <div className="col-span-2">
                    <p className="text-muted">
                        <DateTime date={invoice.createdAt} hideTime /><br />
                        {invoice.status}
                    </p>
                </div>
                <div className="text-right">
                    <Button theme="light"
                        icon="CloudArrowDown"
                        onClick={() => downloadItemisation(invoice.businessId, invoice.itemisationId)}
                        zeroMargin
                    />
                </div>
            </div>
        })}
    </div>
}

export default Billing;
